import { ReactNode } from 'react';
import l from '../../../../lang';
import ButtonLink from '../../../common/button-link';
import { Chip } from '../../../common/chip';
import InputSearch from '../../../common/input-search';
import './styles.scss';
import { useMediaQuery } from '@mui/material';
import PayoutsTransactionFilterGrouped from './filter-grouped';
import useComponentsFilter from './use-components-filter';
import { segmentTransactionsFilterSearchBox } from '../../../../utils/analytics';

interface Filters {
  [key: string]: string;
}

interface PayoutsTransactionFilterProps {
  loading: boolean;
  appliedFilters: Filters;
  clearFilters: () => void;
  changeFilters: (filters: Filters) => void;
  exportButton?: ReactNode;
}

const PayoutsTransactionFilter = ({
  loading,
  appliedFilters,
  clearFilters,
  changeFilters,
  exportButton,
}: PayoutsTransactionFilterProps) => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const { dateRangeFilter, countryFilter, statusFilter } = useComponentsFilter({
    appliedFilters,
    changeFilters,
  });

  const chipText = (keyFilter: string, value: string, allFilters: Filters): { label: string; value: string }[] => {
    switch (keyFilter) {
      case 'status':
        return value.split(',').map((s: string) => ({ label: `${l('payouts.status.' + s)}`, value: s }));
      case 'country':
        return value.split(',').map((co: string) => ({ label: `${l('countryCode.' + co)}`, value: co }));
      case 'from':
        if (value === allFilters.to) {
          return [{ label: value, value }];
        }
        return [{ label: `${l('transactions.filter.chip.' + keyFilter)}: ${value}`, value }];
      case 'to':
        if (value !== allFilters.from) {
          return [{ label: `${l('transactions.filter.chip.' + keyFilter)}: ${value}`, value }];
        }
        return [{ label: '', value: '' }];
      default:
        return [{ label: value, value }];
    }
  };

  const removeItem = (appliedFilter: string, valueToRemove: string) => {
    return appliedFilter.split(',').length > 1
      ? appliedFilter
          .split(',')
          .filter(item => item !== valueToRemove)
          .join(',')
      : '';
  };

  const searchByFilters = [
    { value: 'payout_id', label: l('payouts.filter.search.payoutId') },
    { value: 'beneficiary_first_name', label: l('payouts.filter.search.beneficiaryFirstName') },
    { value: 'beneficiary_last_name', label: l('payouts.filter.search.beneficiaryLastName') },
    { value: 'beneficiary_document', label: l('payouts.filter.search.beneficiaryDocument') },
  ];

  return (
    <div className="transaction_filter">
      <div className="transaction_filter__search">
        <InputSearch
          value={appliedFilters.search_by}
          searchOptions={searchByFilters}
          placeholder={l('payouts.filter.search.placeholder')}
          isLoading={loading}
          helpText={l('payouts.filter.search.placeholder')}
          onSearch={(type, value) => {
            segmentTransactionsFilterSearchBox({ filterType: type, filterText: value });
            changeFilters({ ...appliedFilters, [type]: value });
          }}
        />
      </div>
      {isDesktop ? (
        <>
          <div className="transaction_filter__select">{dateRangeFilter}</div>
          <div className="transaction_filter__select">{countryFilter}</div>
          <div className="transaction_filter__select">{statusFilter}</div>
          {exportButton && <div className="payouts_filter__export">{exportButton}</div>}
        </>
      ) : (
        <div className="transaction_filter__button_filter_wrapper">
          <PayoutsTransactionFilterGrouped
            appliedFilters={appliedFilters}
            clearFilters={clearFilters}
            changeFilters={changeFilters}
          />
        </div>
      )}
      {Object.keys(appliedFilters).some(keyFilter => appliedFilters[keyFilter] !== '') && (
        <>
          <div className="transaction_filter__divider" />
          <div className="transaction_filter__applied_filters_wrapper">
            <div className="transaction_filter__applied_filters">
              <span>{l('transactions.filter.appliedFilters')}: </span>
              {!isDesktop && (
                <>
                  <span className="transaction_filter__clear_filters_mobile">
                    <ButtonLink text={l('transactions.filter.clearFilters')} onClick={clearFilters} />
                  </span>
                  <br />
                </>
              )}
              {Object.keys(appliedFilters).map(keyFilter => {
                const chip =
                  appliedFilters[keyFilter] !== '' && chipText(keyFilter, appliedFilters[keyFilter], appliedFilters);
                return (
                  <>
                    {chip &&
                      chip.map(({ label, value }) => (
                        <>
                          {label && value && (
                            <div className="transaction_filter__chip_wrapper">
                              <Chip
                                label={label}
                                onDelete={() =>
                                  changeFilters({
                                    ...appliedFilters,
                                    ...(keyFilter === 'from' && appliedFilters.from === appliedFilters.to ? { to: '' } : {}),
                                    [keyFilter]: removeItem(appliedFilters[keyFilter], value),
                                  })
                                }
                              />
                            </div>
                          )}
                        </>
                      ))}
                  </>
                );
              })}
            </div>
            <div className="transaction_filter__clear_filters_desktop">
              <ButtonLink text={l('transactions.filter.clearFilters')} onClick={clearFilters} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PayoutsTransactionFilter;
